import { getCache, setCache } from "./cacheService";
import { snapBboxToGrid } from "../utils/boundsUtils";

const SE_ROAD_NETWORK_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/getSwedishRoadNetwork';

export type SwedishRoadAccessClass = 'public' | 'forest_road' | 'tractor_road' | 'service_road';

export interface SwedishRoadFeature {
  type: 'Feature';
  geometry: { type: 'LineString'; coordinates: [number, number][] };
  properties: {
    county: number | null;
    roadNumber: number | null;
    subNumber: number | null;
    length: number | null;
    roadOwnerCode: 1 | 2 | 3 | null;
    roadOwner: string | null;
    wearLayer: number | null;
    roadWidth: number | null;
    speedLimit: number | null;
    bearingCapacity: string | null;
    accessClass: SwedishRoadAccessClass;
    osmId?: number;
    tracktype?: string | null;
    surface?: string | null;
    source?: 'trafikverket' | 'osm';
  };
}

export interface SwedishRoadNetwork {
  type: 'FeatureCollection';
  features: SwedishRoadFeature[];
  meta?: { source: string; geometryRows: number; dataRows: number; keptFeatures: number };
}

const EMPTY: SwedishRoadNetwork = { type: 'FeatureCollection', features: [] };

// Distance from point to line segment, in meters (haversine + projection).
const haversineMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
};

const distancePointToSegmentMeters = (lat: number, lon: number, segLine: [number, number][]): number => {
  let minDist = Infinity;
  for (let i = 0; i < segLine.length - 1; i++) {
    const [lon1, lat1] = segLine[i];
    const [lon2, lat2] = segLine[i + 1];
    // Approximate: distance to nearer endpoint plus midpoint check
    const d1 = haversineMeters(lat, lon, lat1, lon1);
    const d2 = haversineMeters(lat, lon, lat2, lon2);
    minDist = Math.min(minDist, d1, d2);
  }
  return minDist;
};

// Look up the closest Swedish road segment within radiusMeters of a coord.
// Used to enrich newly-created spots in Sweden with roadAccessClass.
export const findNearestSwedishRoad = async (
  coords: { latitude: number, longitude: number },
  radiusMeters = 80,
): Promise<{ accessClass: SwedishRoadAccessClass, distance: number, properties: SwedishRoadFeature['properties'] } | null> => {
  const degBuffer = radiusMeters / 111000; // ~degrees per meter
  const bounds = {
    latMin: coords.latitude - degBuffer,
    latMax: coords.latitude + degBuffer,
    lonMin: coords.longitude - degBuffer * 2, // longitude degrees are shorter at high latitude
    lonMax: coords.longitude + degBuffer * 2,
  };
  const network = await fetchSwedishRoadNetwork(bounds);
  if (!network.features.length) return null;

  let best: { accessClass: SwedishRoadAccessClass, distance: number, properties: SwedishRoadFeature['properties'] } | null = null;
  for (const feat of network.features) {
    const dist = distancePointToSegmentMeters(coords.latitude, coords.longitude, feat.geometry.coordinates);
    if (dist > radiusMeters) continue;
    if (!best || dist < best.distance) {
      best = { accessClass: feat.properties.accessClass, distance: dist, properties: feat.properties };
    }
  }
  return best;
};

export const fetchSwedishRoadNetwork = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<SwedishRoadNetwork> => {
  const cacheKey = `se_road_network_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached as SwedishRoadNetwork;

  try {
    const url = `${SE_ROAD_NETWORK_URL}?latMin=${bounds.latMin}&latMax=${bounds.latMax}&lonMin=${bounds.lonMin}&lonMax=${bounds.lonMax}`;
    const res = await fetch(url);
    if (!res.ok) return EMPTY;
    const data = await res.json() as SwedishRoadNetwork;
    if (!data?.features) return EMPTY;
    setCache(cacheKey, data, 1000 * 60 * 60); // 1h client cache (server cache is 7 days)
    return data;
  } catch (error) {
    console.error('Failed to fetch Swedish road network:', error);
    return EMPTY;
  }
};
